import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Helmet from 'react-helmet'
// Removed ReactLoading import
// Removed react-icons/gr and react-icons/fa imports

// --- API Service (api/dates.js - conceptual file) ---
// This would ideally be in a separate file, but for a single immersive, it's defined here.
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const datesApi = {
  getAll: async () => {
    const res = await axios.get(`${API_BASE_URL}/api/dates`);
    return res.data;
  },
  create: async (data) => {
    const res = await axios.post(`${API_BASE_URL}/api/dates`, data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await axios.patch(`${API_BASE_URL}/api/dates/${id}`, data); // Using PATCH as per original
    return res.data;
  },
  remove: async (id) => {
    await axios.delete(`${API_BASE_URL}/api/dates/${id}`);
  },
};

// --- Custom Hook for Modal Logic ---
const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  return { isOpen, openModal, closeModal, setIsOpen }; // setIsOpen also returned for direct control
};

// --- Custom Hook for Form Handling ---
const useForm = (initialState) => {
  const [formData, setFormData] = useState(initialState);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => setFormData(initialState);

  return { formData, setFormData, handleChange, resetForm };
};

// --- Custom Loading Spinner Component ---
const CustomLoadingSpinner = () => (
  <div className="custom-loading-spinner">
    <div className="spinner-bubble"></div>
    <div className="spinner-bubble"></div>
    <div className="spinner-bubble"></div>
  </div>
);

// --- Inline SVG Icons ---
const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M12 4.5v15m7.5-7.5h-15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);


// ==========================
// CHILD COMPONENT: DateItem
// ==========================
const DateItem = ({ dateItem, deleteHandler, updateHandler }) => {
  const {
    isOpen: isUpdateModalOpen,
    openModal: openUpdateModal,
    closeModal: closeUpdateModal,
  } = useModal();
  const {
    isOpen: isDeleteConfirmModalOpen,
    openModal: openDeleteConfirmModal,
    closeModal: closeDeleteConfirmModal,
  } = useModal();

  const { formData: editData, setFormData: setEditData, handleChange } = useForm({
    title: dateItem.title,
    date: dateItem.date.slice(0, 10), // Ensure date is in 'YYYY-MM-DD' format
    description: dateItem.description,
    classification: dateItem.classification,
    isRecurring: dateItem.isRecurring,
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Effect to update form data if dateItem prop changes (e.g., after a successful update)
  useEffect(() => {
    setEditData({
      title: dateItem.title,
      date: dateItem.date.slice(0, 10),
      description: dateItem.description,
      classification: dateItem.classification,
      isRecurring: dateItem.isRecurring,
    });
  }, [dateItem, setEditData]);

  // Handle classification change to auto-set isRecurring for Birthday
  const handleClassificationChange = (e) => {
    const { value } = e.target;
    setEditData((prev) => ({
      ...prev,
      classification: value,
      isRecurring: value === 'Birthday', // Automatically set recurring for Birthday
    }));
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    setError(null);
    try {
      await datesApi.update(dateItem._id, editData);
      updateHandler(); // Callback to refresh parent's data
      closeUpdateModal();
    } catch (err) {
      console.error('Failed to update date:', err);
      setError(err.response?.data?.error || 'Failed to update date.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    try {
      await datesApi.remove(dateItem._id);
      deleteHandler(); // Callback to refresh parent's data
      closeDeleteConfirmModal();
    } catch (err) {
      console.error('Failed to delete date:', err);
      setError(err.response?.data?.error || 'Failed to delete date.');
    }
  };

  return (
    <div className="date-cap">
      <div className="dateRow">
        <div className="dateNameDiv">
          <em className="dateClassification">{dateItem.classification}</em>
          <strong className="dateTitle">
            {dateItem.title.charAt(0).toUpperCase() + dateItem.title.slice(1)}
          </strong>
          <p className="dateDate">
            {new Date(dateItem.date).toLocaleDateString('en-SG', {
              timeZone: 'Asia/Singapore',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </p>
        </div>
        <div className="dateButtonDiv">
          <button
            className="dateButton"
            onClick={openUpdateModal}
            aria-label={`Edit ${dateItem.title}`}
          >
            <EditIcon />
          </button>
          <button
            className="dateButton"
            onClick={openDeleteConfirmModal}
            aria-label={`Delete ${dateItem.title}`}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
      <div className="dateMeta">
        <p className="dateDescription">{dateItem.description}</p>
      </div>

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && (
        <div className="modalOverlay" role="dialog" aria-modal="true" aria-labelledby="updateModalTitle">
          <div className="modalContent">
            <button className="modal-close-button" onClick={closeUpdateModal} aria-label="Close update modal">
              <CloseIcon />
            </button>
            <h4 id="updateModalTitle" className="modalTitle">Update Date</h4>
            <form className="modalForm" onSubmit={(e) => e.preventDefault()}> {/* Prevent default form submission */}
              <div className="modalInputDiv">
                <label htmlFor="update-title" className="modalInputLabel">Title:</label>
                <input
                  id="update-title"
                  className="modalInput"
                  name="title"
                  value={editData.title}
                  onChange={handleChange}
                />
              </div>

              <div className="modalInputDiv">
                <label htmlFor="update-date" className="modalInputLabel">Date:</label>
                <input
                  id="update-date"
                  className="modalInput"
                  type="date"
                  name="date"
                  value={editData.date}
                  onChange={handleChange}
                />
              </div>

              <div className="modalInputDiv">
                <label htmlFor="update-description" className="modalInputLabel">Description:</label>
                <textarea
                  id="update-description"
                  className="modalTextArea"
                  name="description"
                  value={editData.description}
                  onChange={handleChange}
                />
              </div>

              <div className="modalInputDiv">
                <label htmlFor="update-classification" className="modalInputLabel">Classification:</label>
                <select
                  id="update-classification"
                  className="modalInput"
                  name="classification"
                  value={editData.classification}
                  onChange={handleClassificationChange} // Use specific handler
                >
                  <option value="">Select classification</option>
                  <option value="Birthday">Birthday</option>
                  <option value="Event">Event</option>
                  <option value="Holiday">Holiday</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {editData.classification !== 'Birthday' && (
                <label className="modalCheckboxLabel">
                  <input
                    type="checkbox"
                    name="isRecurring"
                    checked={editData.isRecurring}
                    onChange={handleChange}
                  />
                  Recurring?
                </label>
              )}

              {error && <div className="modalError">{error}</div>}

              {isUpdating ? (
                <div className="loading-div">
                  <CustomLoadingSpinner />
                </div>
              ) : (
                <div className="modalButtonDiv">
                  <button type="button" className="modalButton modalSubmitButton" onClick={handleUpdate}>
                    Update
                  </button>
                  <button type="button" className="modalButton modalCancelButton" onClick={closeUpdateModal}>
                    Cancel
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION */}
      {isDeleteConfirmModalOpen && (
        <div className="modalOverlay" role="dialog" aria-modal="true" aria-labelledby="deleteConfirmTitle">
          <div className="modalAlertDiv">
            <h4 id="deleteConfirmTitle" className="modalTitle">Confirm Delete</h4>
            <p className="modalText">
              Are you sure you want to delete the date: <strong>{dateItem.title}</strong>?
            </p>
            <div className="modalButtonDiv">
              <button className="modalButton modalSubmitButton" onClick={handleDelete}>
                Delete
              </button>
              <button className="modalButton modalCancelButton" onClick={closeDeleteConfirmModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================
// MAIN COMPONENT: Dates
// ==========================
const Dates = () => {
  const [allDates, setAllDates] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const {
    isOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal();
  const [currentPage, setCurrentPage] = useState(1);
  const datesPerPage = 10;

  const { formData, setFormData, handleChange, resetForm } = useForm({
    title: '',
    date: '',
    description: '',
    classification: '',
    isRecurring: false,
  });

  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  const fetchDates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await datesApi.getAll();
      setAllDates(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (err) {
      console.error('Failed to fetch dates:', err);
      setError(err.response?.data?.error || 'Failed to load dates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  // Handle classification change for add form
  const handleAddClassificationChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      classification: value,
      isRecurring: value === 'Birthday',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAdding(true);
    setError(null);

    try {
      await datesApi.create(formData);
      resetForm();
      fetchDates(); // Re-fetch all dates to get the latest list
      closeAddModal();
    } catch (err) {
      console.error('Failed to add date:', err);
      setError(err.response?.data?.error || 'Failed to add date.');
    } finally {
      setIsAdding(false);
    }
  };

  const filteredDates = allDates.filter((item) => {
    const dateObj = new Date(item.date);
    const day = dateObj.getDate().toString();
    const month = dateObj.toLocaleString('default', { month: 'short' }).toLowerCase();
    const year = dateObj.getFullYear().toString();
    const iso = item.date;
    const fullDate = dateObj
      .toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
      .toLowerCase();

    const searchable = [
      item.title,
      item.description,
      item.classification,
      fullDate,
      day,
      month,
      year,
      iso,
    ];

    const lowerCaseSearch = searchValue.toLowerCase();
    return searchable.some((val) => val && val.toLowerCase().includes(lowerCaseSearch));
  });

  const totalPages = Math.max(1, Math.ceil(filteredDates.length / datesPerPage)); // Ensure at least 1 page
  const displayedDates = filteredDates.slice(
    (currentPage - 1) * datesPerPage,
    currentPage * datesPerPage
  );

  return (
    <div className="dates-wrapper">
      <Helmet>
        <title>NavarroJalen | Dates</title>
        <meta name="description" content="NavarroJalen | Dates" />
        <meta property="og:title" content="NavarroJalen | Dates" />
      </Helmet>
      {/* === TOP BAR === */}
      <div className="subMenuBar">
        <button
          className="subMenuBarButton"
          onClick={() => {
            resetForm(); // Reset form when opening add modal
            if (isAddModalOpen) {
              closeAddModal();
            } else {
              openAddModal();
            }
          }}
          aria-label={isAddModalOpen ? 'Close add form' : 'Open add form'}
        >
          {isAddModalOpen ? <CloseIcon /> : <AddIcon />}
        </button>
        <input
          className="searchbar"
          placeholder="Search dates..." // More descriptive placeholder
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setCurrentPage(1); // Reset to first page on search
          }}
          aria-label="Search dates"
        />
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="paginationButton" // Add class for styling
              aria-label="Previous page"
            >
              Prev
            </button>
            <span className="paginationInfo">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="paginationButton" // Add class for styling
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* === ADD MODAL === */}
      {isAddModalOpen && (
        <div className="modalOverlay" role="dialog" aria-modal="true" aria-labelledby="addModalTitle">
          <div className="modalContent">
            <button className="modal-close-button" onClick={closeAddModal} aria-label="Close add date modal">
              <CloseIcon />
            </button>
            <h2 id="addModalTitle" className="modalTitle">Add Date</h2>

            <form className="modalForm" onSubmit={handleSubmit}>
              <div className="modalInputDiv">
                <label htmlFor="add-title" className="modalInputLabel">Title:</label>
                <input
                  id="add-title"
                  className="modalInput"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required // Make title required
                />
              </div>

              <div className="modalInputDiv">
                <label htmlFor="add-classification" className="modalInputLabel">Classification:</label>
                <select
                  id="add-classification"
                  className="modalInput"
                  name="classification"
                  value={formData.classification}
                  onChange={handleAddClassificationChange}
                  required // Make classification required
                >
                  <option value="">Select classification</option>
                  <option value="Birthday">Birthday</option>
                  <option value="Event">Event</option>
                  <option value="Holiday">Holiday</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="modalInputDiv">
                <label htmlFor="add-date" className="modalInputLabel">Date:</label>
                <input
                  id="add-date"
                  className="modalInput"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required // Make date required
                />
              </div>

              {formData.classification !== 'Birthday' && (
                <label className="modalCheckboxLabel">
                  <input
                    type="checkbox"
                    name="isRecurring"
                    checked={formData.isRecurring}
                    onChange={handleChange}
                  />
                  Recurring?
                </label>
              )}

              <div className="modalInputDiv">
                <label htmlFor="add-description" className="modalInputLabel">Description:</label>
                <textarea
                  id="add-description"
                  className="modalTextArea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              {error && <div className="modalError">{error}</div>} {/* Use modalError class for consistency */}

              {isAdding ? (
                <div className="loading-div">
                  <CustomLoadingSpinner />
                </div>
              ) : (
                <div className="modalButtonDiv">
                  <button type="submit" className="modalButton modalSubmitButton">
                    Add Date
                  </button>
                  <button type="button" className="modalButton modalCancelButton" onClick={closeAddModal}>
                    Cancel
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* === DATE LIST DISPLAY === */}
      <div className="datesTable">
        {isLoading ? (
          <div className="loading-div">
            <CustomLoadingSpinner />
          </div>
        ) : displayedDates.length === 0 ? (
          <p className="dates-empty-text">
            No dates found. {searchValue ? 'Try a different search, or add a new date.' : 'Click the "+" button to add your first date!'}
          </p>
        ) : (
          <div className="date-maindiv">
            {displayedDates.map((dateItem) => (
              <DateItem
                key={dateItem._id}
                dateItem={dateItem}
                deleteHandler={fetchDates} // Pass fetchDates to re-fetch after delete
                updateHandler={fetchDates} // Pass fetchDates to re-fetch after update
              />
            ))}
          </div>
        )}
      </div>
      {/* Basic CSS for the custom loading spinner */}
      <style>
        {`
        .custom-loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          height: 50px; /* Match the height of ReactLoading */
        }

        .spinner-bubble {
          width: 12px;
          height: 12px;
          background-color: #66C0F4; /* Match the color of ReactLoading */
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .spinner-bubble:nth-child(1) {
          animation-delay: -0.32s;
        }

        .spinner-bubble:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1.0);
          }
        }
        `}
      </style>
    </div>
  );
};

export default Dates;
